import { grey } from '@mui/material/colors';
import { createTheme, type Theme } from '@mui/material/styles';

const defaultTheme = createTheme();

const { augmentColor } = defaultTheme.palette;

const createColor = (mainColor: string) => augmentColor({ color: { main: mainColor } });

const OVERRIDE_HOVER_COLOR = 'hsl(0 0% 90%)';

const theme: Theme = createTheme({
  ...defaultTheme,
  spacing: 8,
  typography: {
    htmlFontSize: 16,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '24px',
      lineHeight: '24px',
      letterSpacing: '0.00938em',
      fontWeight: '700'
    },
    h2: {
      fontSize: '22px',
      lineHeight: '24px',
      letterSpacing: '0.00938em',
      fontWeight: '700'
    },
    h3: {
      fontSize: '18px',
      lineHeight: '24px',
      letterSpacing: '0.00938em',
      fontWeight: '700'
    },
    h4: {
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.00938em',
      fontWeight: '700'
    },
    h5: {
      fontSize: '14px',
      lineHeight: '24px',
      letterSpacing: '0.00938em',
      fontWeight: '700'
    },
    h6: {
      fontSize: '14px',
      lineHeight: '24px',
      letterSpacing: '0.00938em',
      fontWeight: '700'
    },
    small: {
      fontSize: '12px'
    },
    input: {
      fontSize: '16px'
    },
    smallHeader: {
      fontSize: '12px',
      fontWeight: 700,
      textTransform: 'uppercase'
    }
  },
  added: {
    main: 'hsl(123, 57%, 35%)',
    background: 'hsl(123, 57%, 90%)'
  },
  removed: {
    main: 'hsl(14, 87%, 35%)',
    background: 'hsl(14, 87%, 90%)'
  },
  edited: {
    main: 'hsl(54, 77%, 35%)',
    background: 'hsl(54, 77%, 90%)'
  },
  palette: {
    primary: {
      // Purple
      main: '#5c6bc0',
      light: '#7c88cc',
      dark: '#5539D2'
    },
    secondary: {
      // Green
      main: '#5CD79B',
      light: '#C3EAD7',
      dark: '#1ca56d',
      contrastText: '#fff'
    },
    text: {
      primary: 'rgb(69,90,100)'
    },
    error: {
      main: '#E85746',
      light: '#ff8972',
      dark: '#af221d',
      contrastText: '#fff'
    },
    background: {
      default: '#F5F3F9'
    },
    disabled: createColor('#9e9e9e')
  },
  components: {
    MuiDialogTitle: {
      defaultProps: {
        sx: {
          fontSize: '18px'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          color: '#5c6bc0'
        },
        contained: {
          boxShadow: 'none',
          color: 'white'
        },
        text: {
          color: '#5c6bc0'
        }
      },
      defaultProps: {
        color: 'primary',
        disableElevation: true
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiListItemAvatar: {
      styleOverrides: {
        root: {
          marginRight: '.9em',
          minWidth: '1.875rem'
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: '1.875rem',
          height: '1.875rem',
          backgroundColor: 'rgb(237, 237, 237)'
        },
        colorDefault: {
          backgroundColor: grey[200]
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'rgb(69,90,100)'
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: 'rgba(0, 0, 0, 0.12)'
        }
      },
      defaultProps: {
        variant: 'outlined'
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: OVERRIDE_HOVER_COLOR
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: OVERRIDE_HOVER_COLOR
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          overflow: 'visible'
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          overflow: 'visible'
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none'
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '0',
          height: '100%'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: '0',
          textTransform: 'none',
          [defaultTheme.breakpoints.up(0)]: {
            minWidth: '0'
          },
          '&$selected': {
            color: '#5c6bc0'
          }
        },
        labelIcon: {
          minHeight: '0'
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined'
      }
    }
  },
  mixins: {
    MuiDataGrid: {
      // Headers, and top & bottom fixed rows
      containerBackground: '#fff'
    }
  },
  zIndex: {
    drawer: 298
  }
});

export default theme;
